import Card from '@/global/components/Card'
import { ACOES, isAcaoId, type RotinaInput } from '@acme/core'
import {
    closestCenter,
    DndContext,
    DragOverlay,
    type DraggableAttributes,
    type DraggableSyntheticListeners,
    type DragEndEvent,
    type DragStartEvent,
    type DropAnimation,
    defaultDropAnimationSideEffects,
    KeyboardSensor,
    PointerSensor,
    type UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    restrictToParentElement,
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import IconButton from 'global/components/Button/IconButton'
import { GripVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { forwardRef, type HTMLAttributes, useState } from 'react'
import { createPortal } from 'react-dom'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { type Acao, AcaoDialog, IconeAcao } from './AcaoDialog'
import { ListaField } from './ListaField'

const MODIFICADORES_ARRASTO = [restrictToVerticalAxis, restrictToParentElement]

/** Rolagem automática só no eixo vertical e perto da borda, para não disparar ao arrastar dentro da lista. */
const AUTO_SCROLL = { threshold: { x: 0, y: 0.1 }, acceleration: 5 }

const ANIMACAO_SOLTAR: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: { active: { opacity: '0.4' } },
    }),
}

/** Lista ordenável das ações ("Então"): arrastar ou setas definem a ordem de execução. */
export function AcoesField() {
    const { control } = useFormContext<RotinaInput>()
    const { fields, append, remove, move, update } = useFieldArray({
        control,
        name: 'acoes',
    })
    const [editando, setEditando] = useState<number | 'nova' | null>(null)
    const [arrastandoId, setArrastandoId] = useState<UniqueIdentifier | null>(
        null,
    )
    const arrastando = fields.find(it => it.id === arrastandoId)
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )

    const iniciarArrasto = ({ active }: DragStartEvent) =>
        setArrastandoId(active.id)

    const moverAoSoltar = ({ active, over }: DragEndEvent) => {
        setArrastandoId(null)
        if (!over || active.id === over.id) return
        move(
            fields.findIndex(it => it.id === active.id),
            fields.findIndex(it => it.id === over.id),
        )
    }

    const salvarAcao = (acao: Acao) => {
        if (editando === 'nova') append(acao)
        else if (editando !== null) update(editando, acao)
        setEditando(null)
    }

    return (
        <>
            <ListaField
                name="acoes"
                rotuloAdicionar="Adicionar ação"
                onAdicionar={() => setEditando('nova')}
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={MODIFICADORES_ARRASTO}
                    autoScroll={AUTO_SCROLL}
                    onDragStart={iniciarArrasto}
                    onDragEnd={moverAoSoltar}
                    onDragCancel={() => setArrastandoId(null)}
                >
                    <SortableContext
                        items={fields}
                        strategy={verticalListSortingStrategy}
                    >
                        {fields.map((field, index) => (
                            <AcaoItem
                                key={field.id}
                                id={field.id}
                                acaoId={field.acaoId}
                                onEditar={() => setEditando(index)}
                                onRemover={() => remove(index)}
                            />
                        ))}
                    </SortableContext>
                    {/* Portal: o translate do Dialog desloca o position:fixed do overlay. */}
                    {createPortal(
                        <DragOverlay dropAnimation={ANIMACAO_SOLTAR}>
                            {arrastando && (
                                <AcaoCard
                                    acaoId={arrastando.acaoId}
                                    className="cursor-grabbing shadow-xl ring-2 ring-primary"
                                />
                            )}
                        </DragOverlay>,
                        document.body,
                    )}
                </DndContext>
            </ListaField>
            {editando !== null && (
                <AcaoDialog
                    valor={
                        editando === 'nova'
                            ? undefined
                            : (fields[editando] as Acao)
                    }
                    onClose={() => setEditando(null)}
                    onSalvar={salvarAcao}
                />
            )}
        </>
    )
}

interface AcaoItemProps {
    id: string
    acaoId: string
    onEditar: () => void
    onRemover: () => void
}

function AcaoItem({ id, acaoId, onEditar, onRemover }: AcaoItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id })

    return (
        <AcaoCard
            ref={setNodeRef}
            acaoId={acaoId}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={cn(
                isDragging &&
                    'border-2 border-dashed border-primary opacity-40 shadow-none',
            )}
            alcaArrasto={{ attributes, listeners }}
            onEditar={onEditar}
            onRemover={onRemover}
        />
    )
}

interface AcaoCardProps extends HTMLAttributes<HTMLDivElement> {
    acaoId: string
    /** Props do sortable aplicadas à alça; ausente no clone do {@link DragOverlay}. */
    alcaArrasto?: {
        attributes: DraggableAttributes
        listeners: DraggableSyntheticListeners
    }
    onEditar?: () => void
    onRemover?: () => void
}

const AcaoCard = forwardRef<HTMLDivElement, AcaoCardProps>(
    ({ acaoId, alcaArrasto, onEditar, onRemover, className, ...rest }, ref) => {
        const definicao = isAcaoId(acaoId) ? ACOES[acaoId] : undefined

        return (
            <Card
                ref={ref}
                className={cn(
                    'flex flex-wrap items-center gap-3 p-3',
                    className,
                )}
                {...rest}
            >
                <IconButton
                    type="button"
                    aria-label="Arrastar para reordenar"
                    className="cursor-grab text-muted-foreground active:cursor-grabbing"
                    {...alcaArrasto?.attributes}
                    {...alcaArrasto?.listeners}
                >
                    <GripVerticalIcon />
                </IconButton>
                {isAcaoId(acaoId) && <IconeAcao acaoId={acaoId} />}
                <div className="min-w-0 flex-1 basis-40">
                    <p className="font-semibold">
                        {definicao?.label ?? acaoId}
                    </p>
                    <p className="truncate text-sm text-muted-foreground">
                        {definicao?.descricao ?? ''}
                    </p>
                </div>
                <IconButton title="Editar ação" onClick={onEditar}>
                    <PencilIcon />
                </IconButton>
                <IconButton
                    title="Remover ação"
                    color="error"
                    onClick={onRemover}
                >
                    <Trash2Icon />
                </IconButton>
            </Card>
        )
    },
)

AcaoCard.displayName = 'AcaoCard'
